import axios from "axios";

const API_FILES = `${process.env.REACT_APP_API}/api/v1/files`;

/** Use direct browser→S3 multipart when larger than this (5 MiB; aligns with S3 multipart part rules). */
export const MULTIPART_THRESHOLD_BYTES = 5 * 1024 * 1024;

/** S3 part size (8 MiB). Must be ≥ 5 MiB for all but the last part. */
export const MULTIPART_PART_SIZE_BYTES = 8 * 1024 * 1024;

/**
 * @param {number} mb — megabytes from settings.maxFileSize
 */
export function megabytesToBytes(mb) {
  return mb * 1024 * 1024;
}

/**
 * Prefer the API `message` on Axios errors (e.g. unsupported MIME type).
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function uploadErrorMessage(error, fallback = "Upload failed") {
  if (typeof error === "string" && error) return error;
  const apiMessage = error?.response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage) return apiMessage;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

async function uploadMultipart(file, {userId, label, visibility}) {
  const initBody = {
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    byteSize: file.size,
    visibility
  };

  const {data: initData, status: initStatus} = await axios.post(
    `${API_FILES}/multipart/init`,
    initBody,
    {
      params: {userId, ...(label ? {label} : {})}
    }
  );

  if (initStatus !== 201 || !initData?.data)
    throw new Error(initData?.message || "Could not start upload");

  const {
    key,
    uploadId,
    partSizeBytes,
    parts: partDescriptors,
    completeToken,
    completeTokenExpires
  } = initData.data;

  const abortMpu = async () => {
    try {
      await axios.post(
        `${API_FILES}/multipart/abort`,
        {
          key,
          uploadId,
          contentType: file.type || "application/octet-stream",
          byteSize: file.size,
          completeToken,
          completeTokenExpires,
          visibility
        },
        {params: {userId}}
      );
    } catch {
      // best-effort abort
    }
  };

  let uploadedParts;
  try {
    uploadedParts = await Promise.all(
      partDescriptors.map(async ({partNumber, url}) => {
        const start = (partNumber - 1) * partSizeBytes;
        const end = Math.min(start + partSizeBytes, file.size);
        const chunk = file.slice(start, end);

        let putRes;
        try {
          // Do not set Content-Type: presigned upload_part URLs are signed without it.
          // Sending a type (e.g. text/plain) causes SignatureDoesNotMatch on S3.
          putRes = await fetch(url, {
            method: "PUT",
            mode: "cors",
            credentials: "omit",
            body: chunk
          });
        } catch (e) {
          // Browser "Failed to fetch" is commonly CORS/mixed-content when PUT-ing to S3.
          // Give a higher-signal error so we don’t silently mask infra misconfig.
          const msg = typeof e === "string" ? e : e?.message || "Failed to fetch";
          throw new Error(
            `Part ${partNumber} upload failed (${msg}). This is often caused by S3 CORS configuration not allowing PUT from the app origin, or by mixed-content blocking (http app -> https S3).`
          );
        }

        if (!putRes.ok) {
          const text = await putRes.text().catch(() => "");
          const code = text.match(/<Code>([^<]+)<\/Code>/)?.[1];
          const s3Message = text.match(/<Message>([^<]+)<\/Message>/)?.[1];
          throw new Error(
            s3Message && code
              ? `${code}: ${s3Message}`
              : text || `Part ${partNumber} upload failed (${putRes.status})`
          );
        }

        // ETag may be hidden by S3 CORS; complete endpoint resolves parts via list_parts.
        const etag = putRes.headers.get("etag");
        return etag ? {partNumber, etag} : {partNumber};
      })
    );
  } catch (e) {
    await abortMpu();
    throw e;
  }

  uploadedParts.sort((a, b) => a.partNumber - b.partNumber);

  const {data: doneData, status: doneStatus} = await axios.post(
    `${API_FILES}/multipart/complete`,
    {
      key,
      uploadId,
      parts: uploadedParts,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      byteSize: file.size,
      visibility,
      completeToken,
      completeTokenExpires
    },
    {params: {userId}}
  );

  if (doneStatus !== 201 || !doneData?.data)
    throw new Error(doneData?.message || "Could not finalize upload");
  return doneData.data;
}

/**
 * Upload one file: multipart presigned to S3 when over threshold, else FormData POST to /files.
 *
 * @param {File} file
 * @param {{ userId: string, maxBytes: number, label?: string, visibility?: string }} opts
 * @returns {Promise<{ link: string, id: number, [k: string]: unknown }>}
 */
export async function uploadSingleFile(file, {userId, maxBytes, label, visibility = "private"}) {
  try {
    if (file.size > maxBytes) {
      throw new Error(`File exceeds maximum size of ${Math.round(maxBytes / (1024 * 1024))} MB`);
    }

    if (file.size > MULTIPART_THRESHOLD_BYTES) {
      return await uploadMultipart(file, {userId, label, visibility});
    }

    const formData = new FormData();
    formData.append("file-0", file, file.name);
    const params = {userId};
    if (label) params.label = label;
    if (visibility) params.visibility = visibility;

    const post = () =>
      axios.post(API_FILES, formData, {
        params,
        headers: {"Content-Type": "multipart/form-data"}
      });

    let response;
    try {
      response = await post();
    } catch (e) {
      // No response means the connection dropped mid-upload; try once more before failing.
      if (e?.response || e?.code === "ERR_CANCELED") throw e;
      response = await post();
    }

    const {data, status} = response;

    if (status !== 201 || !data?.data) throw new Error(data?.message || "Upload failed");
    return data.data;
  } catch (e) {
    throw new Error(uploadErrorMessage(e));
  }
}

/**
 * Upload many files via uploadSingleFile; oversize files go to failed without uploading.
 *
 * @param {File[]} files
 * @param {{ userId: string, maxBytes: number, label?: string, visibility?: string }} opts
 * @returns {Promise<{ uploaded: object[], failed: File[], error: string|null }>}
 */
export async function uploadFilesInBatch(files, {userId, maxBytes, label, visibility = "private"}) {
  const list = Array.from(files);
  const failed = [];
  const toUpload = [];
  let error = null;

  list.forEach(file => {
    if (file.size > maxBytes) failed.push(file);
    else toUpload.push(file);
  });

  const uploaded = await Promise.all(
    toUpload.map(file =>
      uploadSingleFile(file, {userId, maxBytes, label, visibility}).catch(e => {
        failed.push(file);
        if (!error) error = uploadErrorMessage(e);
        return null;
      })
    )
  );

  return {uploaded: uploaded.filter(Boolean), failed, error};
}
